import React from 'react';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { formatEventDateTime } from '../utils/eventTypes';
import PersonAvatar from '../shared/PersonAvatar';
import { UI_TIMINGS } from '../../../config/appConfig';
import { getInterestLevelColor, shouldInterestLevelPulse } from '../utils/interestLevel';
import { useSearch } from '../../../contexts/searchContext';

/**
 * MatchEventListItem - Displays a match event with person image and name
 * @param {Object} event - Event data
 * @param {Function} onClick - Callback when item is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {boolean} disabled - Whether the item is disabled (e.g., during loading)
 * @param {boolean} showDelete - Whether to show the delete button
 */
function MatchEventListItem({ event, onClick, onDelete, disabled = false, showDelete = true }) {
  const { datasetObjects } = useSearch();

  const dateTimeStr = formatEventDateTime(event.createdAt);
  const personName = event.metadata?.personName || 'Unknown';

  // Shorten to first two words and limit to 20 chars
  const words = personName.split(' ');
  const firstTwoWords = words.length > 2 ? words.slice(0, 2).join(' ') : personName;
  const displayName = firstTwoWords.length > 20 ? `${firstTwoWords.substring(0, 20)}...` : firstTwoWords;

  // Backend provides full URLs - no conversion needed
  const personImage = event.metadata?.personImage;
  const matchSource = event.metadata?.matchSource;
  const capturedImageUrl = event.metadata?.capturedImageUrl;

  // Get dataset icon from person object
  const person = event.metadata?.person;
  const datasetId = person?.dataset;
  const dataset = datasetObjects.find(ds => ds.dataset_id === datasetId);
  const datasetIconUrl = dataset?.icon_url;

  // Interest level indicator
  // Unified schema: read from interest object
  const interestLevel = event.metadata?.person?.interest?.level;
  const interestReason = event.metadata?.person?.interest?.reason;
  const interestColor = getInterestLevelColor(interestLevel);
  const shouldPulse = shouldInterestLevelPulse(interestLevel);

  // Detect if this is a multi-face capture by counting person IDs in filename
  // Format: timestamp_personId1_personId2_personId3.jpg
  const isMultiFace = React.useMemo(() => {
    if (!capturedImageUrl) return false;
    const filename = capturedImageUrl.split('/').pop(); // Get filename
    const withoutExt = filename.replace(/\.(jpg|jpeg|png)$/i, ''); // Remove extension
    const parts = withoutExt.split('_'); // Split by underscore
    // First part is timestamp, rest are person IDs
    return parts.length > 2; // More than timestamp + 1 person ID = multi-face
  }, [capturedImageUrl]);

  const [swipeX, setSwipeX] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);
  const [isMouseDown, setIsMouseDown] = React.useState(false);
  const startX = React.useRef(0);
  const currentX = React.useRef(0);

  const handleDeleteClick = React.useCallback((e) => {
    e.stopPropagation();
    onDelete(event);
  }, [onDelete, event]);

  const handleClick = React.useCallback(() => {
    if (!isSwiping) {
      onClick(event);
    }
  }, [onClick, event, isSwiping]);

  const handleStart = (clientX) => {
    startX.current = clientX;
    currentX.current = clientX;
    setIsSwiping(false);
  };

  const handleMove = (clientX) => {
    currentX.current = clientX;
    const diff = currentX.current - startX.current;

    // Only allow left swipe (negative values)
    if (diff < 0) {
      setSwipeX(Math.max(diff, -100)); // Limit to -100px
      if (Math.abs(diff) > 10) {
        setIsSwiping(true);
      }
    }
  };

  const handleEnd = () => {
    const diff = currentX.current - startX.current;

    // If swiped more than 60px, trigger delete
    if (diff < -60) {
      onDelete(event);
    } else {
      // Reset position
      setSwipeX(0);
      setTimeout(() => setIsSwiping(false), UI_TIMINGS.SWIPE_ANIMATION_CLEANUP);
    }
    setIsMouseDown(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (isMouseDown) {
      handleMove(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  const handleMouseLeave = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  // Color scheme based on context (matches aggregator UI)
  const isInAssignment = !!event.metadata?.assignmentEventId;
  const backgroundColor = isInAssignment
    ? '#f5f5f5'  // Light grey for events in assignments (matches aggregator)
    : matchSource === 'face_match'
    ? '#eceff1'  // Cool grey for AI matches
    : '#e8eaf6';  // Muted indigo for human matches
  const borderColor = isInAssignment
    ? '#ffcc80'  // Light orange for events in assignments (matches aggregator)
    : matchSource === 'face_match'
    ? 'rgba(21, 101, 192, 0.3)'
    : 'rgba(106, 27, 154, 0.3)';

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 1,
        overflow: 'hidden',
        borderRadius: 1
      }}
    >
      {/* Dataset icon - top left corner */}
      {datasetIconUrl && (
        <Tooltip title={dataset?.dataset_name } arrow placement="right">
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              left: 10,
              width: 24,
              height: 24,
              zIndex: 2,
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}
          >
            <img
              src={datasetIconUrl}
              alt='Dataset Icon'
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        </Tooltip>
      )}

      {/* Delete background - revealed during swipe */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 100,
          backgroundColor: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          zIndex: 0
        }}
      >
        <DeleteIcon />
      </Box>

      {/* Main list item - slides left on swipe */}
      <ListItem
        disablePadding
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          height: 80,
          backgroundColor: `${backgroundColor} !important`,
          borderLeft: isInAssignment ? `6px solid ${borderColor}` : `6px solid ${borderColor}`,
          borderRadius: 1,
          pl: 2, // Indent content to show hierarchy
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
          cursor: isMouseDown ? 'grabbing' : 'grab',
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: backgroundColor,
            zIndex: -1
          }
        }}
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Assignment indicator and match type indicators in same column */}
            {(event.metadata?.assignmentEventId || matchSource === 'face_match' || event.metadata?.statementCount > 0) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                {event.metadata?.assignmentEventId && (
                  <Tooltip title={event.metadata?.assignmentDescription || event.metadata.assignmentEventId} arrow>
                    <AssignmentIcon sx={{ fontSize: 16, color: '#1976d2', opacity: 0.7 }} />
                  </Tooltip>
                )}
                {matchSource === 'face_match' && (
                  <Tooltip title={`Match accuracy: ${Math.round((event.metadata?.matchAccuracy || 0) * 100)}%`} arrow>
                    <AutoAwesomeIcon
                      sx={{
                        color: 'primary.main',
                        fontSize: 16
                      }}
                    />
                  </Tooltip>
                )}
                {matchSource === 'face_match' && isMultiFace && (
                  <GroupIcon
                    sx={{
                      color: 'primary.main',
                      fontSize: 16
                    }}
                  />
                )}
                {event.metadata?.statementCount > 0 && (
                  <Tooltip title={`${event.metadata.statementCount} witness statement${event.metadata.statementCount !== 1 ? 's' : ''}`} arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <EditNoteIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                      <Typography variant="caption" sx={{ color: '#9c27b0', fontWeight: 600, fontSize: 10 }}>
                        {event.metadata.statementCount}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
            {false && showDelete && (
              <IconButton
                edge="end"
                aria-label="delete"
                size="small"
                onClick={handleDeleteClick}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      >
        <ListItemButton
          onClick={handleClick}
          disabled={disabled}
          disableRipple={isSwiping}
          sx={{
            height: '100%',
            backgroundColor: 'inherit',
            '&:hover': {
              backgroundColor: 'inherit'
            },
            '& .MuiTouchRipple-root': {
              display: 'none'
            }
          }}
        >
          <Tooltip title={interestReason || (interestLevel ? `Interest: ${interestLevel}` : '')} arrow placement="right" disableHoverListener={!interestLevel}>
            <Box
              onClick={(e) => {
                const personUri = event.metadata?.person?.uri;
                if (personUri) {
                  e.stopPropagation();
                  window.open(personUri, '_blank', 'noreferrer');
                }
              }}
              sx={{
                cursor: event.metadata?.person?.uri ? 'pointer' : 'default',
                '&:hover': event.metadata?.person?.uri ? {
                  opacity: 0.8
                } : {},
                border: interestLevel ? `3px solid ${interestColor}` : 'none',
                borderRadius: '50%',
                padding: interestLevel ? '2px' : 0,
                display: 'inline-flex',
                mr: 2,
                '& .MuiAvatar-root': { mr: 0 },
                animation: shouldPulse ? 'pulse 1.5s ease-in-out infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': {
                    boxShadow: `0 0 0 0 ${interestColor}99`,
                    transform: 'scale(1)'
                  },
                  '50%': {
                    boxShadow: `0 0 0 12px ${interestColor}00`,
                    transform: 'scale(1.05)'
                  }
                }
              }}
            >
              <PersonAvatar imageUrl={personImage} name={personName} size={64} />
            </Box>
          </Tooltip>
          <ListItemText
            primary={displayName}
            secondary={dateTimeStr}
          />
        </ListItemButton>
      </ListItem>
    </Box>
  );
}

export default React.memo(MatchEventListItem);
